import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CountdownContainer = styled.section`
  padding: 4rem 0;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2.5rem 0;
  }
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color, theme }) => $color || theme.colors.primary.blue};
  opacity: 0.1;
  filter: blur(2px);
`;

const GeometricShape = styled(motion.div)`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  background: ${({ theme }) => `linear-gradient(45deg, ${theme.colors.primary.blue}20, ${theme.colors.primary.yellow}20)`};
  border: 1px solid ${({ theme }) => theme.colors.primary.cyan}30;
  opacity: 0.3;
  
  ${({ $shape }) => $shape === 'triangle' && `
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  `}
  
  ${({ $shape }) => $shape === 'hexagon' && `
    clip-path: polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%);
  `}
  
  ${({ $shape }) => $shape === 'diamond' && `
    transform: rotate(45deg);
  `}
`;

const CodeStream = styled(motion.div)`
  position: absolute;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary.cyan};
  opacity: 0.2;
  white-space: nowrap;
  pointer-events: none;
`;

const CountdownHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const CountdownTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
    margin-bottom: 0.75rem;
  }
`;

const CountdownSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 0 0.5rem;
  }
`;

const CountdownContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 1.5rem;
    padding: 0 1rem;
  }
`;

const CountdownTimer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 1rem;
  }
`;

const TimeRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.75rem;
  }
`;

const TimeUnit = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 15px;
  padding: 2rem 1.5rem;
  text-align: center;
  min-width: 120px;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255, 229, 0, 0.1), transparent);
    animation: shimmer 3s linear infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.blue};
    background: rgba(0, 128, 214, 0.1);
    transform: scale(1.05);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 75px;
    padding: 1rem 0.75rem;
    border-radius: 10px;
    
    &:hover {
      transform: none;
    }
  }
`;

const TimeNumber = styled.span`
  font-size: 3rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary.yellow};
  display: block;
  font-family: ${({ theme }) => theme.fonts.mono};
  text-shadow: 0 0 20px rgba(255, 229, 0, 0.5);
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
    text-shadow: 0 0 15px rgba(255, 229, 0, 0.4);
  }
`;

const TimeLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 0.5rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    letter-spacing: 0.5px;
    margin-top: 0.25rem;
  }
`;

const CountdownIllustration = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const HackingTerminal = styled(motion.div)`
  width: 400px;
  height: 300px;
  background: #0A0A0F;
  border: 2px solid ${({ theme }) => theme.colors.primary.cyan};
  border-radius: 10px;
  padding: 1rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  box-shadow: 
    0 0 20px rgba(0, 212, 255, 0.3),
    inset 0 0 20px rgba(0, 212, 255, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.1), transparent);
    animation: scanLine 3s linear infinite;
  }
  
  @keyframes scanLine {
    0% { left: -100%; }
    100% { left: 100%; }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 320px;
    height: 250px;
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary.cyan};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 0.75rem;
    padding-bottom: 0.4rem;
  }
`;

const TerminalButton = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 5px ${({ $color }) => $color};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 10px;
    height: 10px;
  }
`;

const TerminalTitle = styled.div`
  color: ${({ theme }) => theme.colors.primary.cyan};
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
  }
`;

const TerminalContent = styled.div`
  color: ${({ theme }) => theme.colors.primary.cyan};
  font-size: 0.85rem;
  line-height: 1.4;
  height: calc(100% - 3rem);
  overflow: hidden;
  text-align: left;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    line-height: 1.3;
    height: calc(100% - 2.5rem);
  }
`;

const TerminalLine = styled(motion.div)`
  margin-bottom: 0.3rem;
  text-align: left;
  
  .prompt {
    color: ${({ theme }) => theme.colors.primary.yellow};
  }
  
  .command {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  .output {
    color: ${({ theme }) => theme.colors.primary.cyan};
    margin-left: 1rem;
  }
  
  .error {
    color: #E74C3C;
  }
  
  .success {
    color: #2ECC71;
  }
  
  .highlight {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-weight: bold;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 0.2rem;
    
    .output {
      margin-left: 0.5rem;
    }
  }
`;

const BlinkingCursor = styled.span`
  background: ${({ theme }) => theme.colors.primary.cyan};
  animation: blink 1s infinite;
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const MatrixRain = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  opacity: 0.1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    opacity: 0.05;
  }
`;

const MatrixColumn = styled.div`
  position: absolute;
  color: ${({ theme }) => theme.colors.primary.cyan};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.8rem;
  white-space: pre;
  animation: matrixFall ${({ $duration }) => $duration}s linear infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  
  @keyframes matrixFall {
    0% { transform: translateY(-100vh); }
    100% { transform: translateY(100vh); }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.6rem;
  }
`;

const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [terminalLines, setTerminalLines] = useState([]);
  const [currentSequence, setCurrentSequence] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isSequenceComplete, setIsSequenceComplete] = useState(false);
  const timeoutRef = useRef(null);
// eslint-disable-next-line
  const hackingSequences = [
    [
      { type: 'prompt', text: 'root@bsides:~$', delay: 500 },
      { type: 'command', text: ' ./initiate_conference.sh', delay: 1500 },
      { type: 'output', text: 'Initializing Security BSides Albuquerque 2025...', delay: 1000 },
      { type: 'output', text: 'Loading cybersecurity protocols...', delay: 800 },
      { type: 'success', text: '✓ Security frameworks loaded', delay: 600 },
      { type: 'success', text: '✓ Speaker database connected', delay: 600 },
      { type: 'success', text: '✓ Venue systems online', delay: 600 },
      { type: 'output', text: '', delay: 500 },
      { type: 'prompt', text: 'root@bsides:~$', delay: 800 },
      { type: 'command', text: ' countdown --event="BSides ABQ"', delay: 1200 },
      { type: 'output', text: 'Calculating time to breach...', delay: 1000 }
    ],
    [
      { type: 'prompt', text: 'hacker@terminal:~$', delay: 500 },
      { type: 'command', text: ' nmap -sV conference.bsidesabq.org', delay: 1800 },
      { type: 'output', text: 'Starting Nmap scan...', delay: 1000 },
      { type: 'success', text: 'Host is up (0.001s latency)', delay: 800 },
      { type: 'output', text: 'PORT    STATE SERVICE VERSION', delay: 600 },
      { type: 'output', text: '80/tcp  open  http    Apache/2.4', delay: 500 },
      { type: 'output', text: '443/tcp open  https   OpenSSL', delay: 500 },
      { type: 'success', text: 'Conference portal detected!', delay: 800 },
      { type: 'output', text: '', delay: 500 },
      { type: 'prompt', text: 'hacker@terminal:~$', delay: 800 },
      { type: 'command', text: ' register --event bsides2025', delay: 1200 }
    ],
    [
      { type: 'prompt', text: 'pentester@lab:~$', delay: 500 },
      { type: 'command', text: ' python3 exploit_calendar.py', delay: 1600 },
      { type: 'output', text: 'Exploiting calendar system...', delay: 1000 },
      { type: 'highlight', text: 'TARGET: July 25-26, 2025', delay: 800 },
      { type: 'highlight', text: 'LOCATION: Albuquerque, NM', delay: 600 },
      { type: 'success', text: '✓ Dates confirmed in system', delay: 700 },
      { type: 'success', text: '✓ Venue coordinates locked', delay: 600 },
      { type: 'output', text: 'Payload: CYBERSECURITY_KNOWLEDGE', delay: 800 },
      { type: 'success', text: 'Injection successful!', delay: 600 },
      { type: 'output', text: '', delay: 500 },
      { type: 'prompt', text: 'pentester@lab:~$', delay: 800 }
    ]
  ];

  useEffect(() => {
    const eventDate = new Date('2025-07-25T09:00:00-06:00').getTime();
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = eventDate - now;
      
      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);

useEffect(() => {
  const currentCommands = hackingSequences[currentSequence];
  
  // Clear existing timeouts
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
  }

  // If we haven't finished typing the current sequence
  if (currentLineIndex < currentCommands.length) {
    const currentCommand = currentCommands[currentLineIndex];
    
    timeoutRef.current = setTimeout(() => {
      // Add the current line to terminal
      setTerminalLines(prev => [...prev, {
        ...currentCommand,
        id: `${currentSequence}-${currentLineIndex}`
      }]);
      
      // Move to next line
      setCurrentLineIndex(currentLineIndex + 1);
      
    }, currentCommand.delay);
  }
  
  // Cleanup timeout on unmount
  return () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
  // eslint-disable-next-line
}, [currentLineIndex, currentSequence]); // Only depend on these two values

// Also add this useEffect to handle sequence completion:
useEffect(() => {
  const currentCommands = hackingSequences[currentSequence];
  
  // If we've completed the current sequence
  if (currentLineIndex >= currentCommands.length && !isSequenceComplete) {
    setIsSequenceComplete(true);
    
    // Add countdown info after sequence completes
    setTimeout(() => {
      setTerminalLines(prev => [
        ...prev,
        { 
          type: 'highlight', 
          text: `DAYS: ${timeLeft.days}`, 
          id: `${currentSequence}-countdown-1`
        }
      ]);
    }, 500);
    
    setTimeout(() => {
      setTerminalLines(prev => [
        ...prev,
        { 
          type: 'highlight', 
          text: `TIME: ${timeLeft.hours.toString().padStart(2, '0')}:${timeLeft.minutes.toString().padStart(2, '0')}:${timeLeft.seconds.toString().padStart(2, '0')}`, 
          id: `${currentSequence}-countdown-2`
        }
      ]);
    }, 1000);

    setTimeout(() => {
      setTerminalLines(prev => [
        ...prev,
        { 
          type: 'success', 
          text: 'Conference breach scheduled!', 
          id: `${currentSequence}-final`
        }
      ]);
    }, 1500);

    // Reset for next sequence (optional - remove if you don't want looping)
    setTimeout(() => {
      setTerminalLines([]);
      setCurrentSequence(prev => (prev + 1) % hackingSequences.length);
      setCurrentLineIndex(0);
      setIsSequenceComplete(false);
    }, 4000);
  }
  // eslint-disable-next-line
}, [currentLineIndex, currentSequence, isSequenceComplete, timeLeft]);

  const backgroundElements = [
    ...Array.from({ length: 8 }, (_, i) => (
      <FloatingElement
        key={`circle-${i}`}
        $size={30 + Math.random() * 50}
        $color={i % 2 === 0 ? '#0080D650' : '#FFE50050'}
        initial={{ 
          x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
          y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
          opacity: 0 
        }}
        animate={{
          x: [null, Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200)],
          y: [null, Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800)],
          opacity: [0, 0.3, 0]
        }}
        transition={{
          duration: 15 + Math.random() * 10,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }}
      />
    )),
    
    ...Array.from({ length: 6 }, (_, i) => {
      const shapes = ['triangle', 'hexagon', 'diamond'];
      return (
        <GeometricShape
          key={`shape-${i}`}
          $size={40 + Math.random() * 60}
          $shape={shapes[i % shapes.length]}
          initial={{ 
            rotate: 0,
            scale: 0.5,
            opacity: 0
          }}
          animate={{
            rotate: [0, 360],
            scale: [0.5, 1, 0.5],
            opacity: [0, 0.4, 0]
          }}
          transition={{
            duration: 20 + Math.random() * 10,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            left: `${10 + Math.random() * 80}%`,
            top: `${10 + Math.random() * 80}%`
          }}
        />
      );
    }),
    
    ...Array.from({ length: 4 }, (_, i) => (
      <CodeStream
        key={`code-${i}`}
        initial={{ x: '-100%', opacity: 0 }}
        animate={{
          x: ['100vw'],
          opacity: [0, 0.5, 0]
        }}
        transition={{
          duration: 12 + Math.random() * 8,
          repeat: Infinity,
          delay: i * 3,
          ease: "linear"
        }}
        style={{
          top: `${20 + i * 20}%`,
          fontSize: `${10 + Math.random() * 4}px`
        }}
      >
        {`function initSecurity() { return "BSides2025"; } // 0x${Math.random().toString(16).slice(2, 8)}`}
      </CodeStream>
    ))
  ];

  const matrixChars = "01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
  const matrixColumns = Array.from({ length: 20 }, (_, i) => {
    const chars = Array.from({ length: 30 }, () => 
      matrixChars.charAt(Math.floor(Math.random() * matrixChars.length))
    ).join('\n');
    
    return (
      <MatrixColumn
        key={i}
        style={{ left: `${i * 5}%` }}
        $duration={3 + Math.random() * 2}
        $delay={Math.random() * 2}
      >
        {chars}
      </MatrixColumn>
    );
  });

  return (
    <CountdownContainer id="countdown">
      <AnimatedBackground>
        {backgroundElements}
      </AnimatedBackground>
      
      <div className="container">
        <CountdownHeader>
          <CountdownTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            🚨 SECURITY BREACH COUNTDOWN
          </CountdownTitle>
          <CountdownSubtitle>Time until the conference begins</CountdownSubtitle>
        </CountdownHeader>
        
        <CountdownContent>
          <CountdownTimer>
            <TimeRow>
              <TimeUnit
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <TimeNumber>
                  {timeLeft.days}
                </TimeNumber>
                <TimeLabel>Days</TimeLabel>
              </TimeUnit>
              
              <TimeUnit
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <TimeNumber>
                  {timeLeft.hours.toString().padStart(2, '0')}
                </TimeNumber>
                <TimeLabel>Hours</TimeLabel>
              </TimeUnit>
            </TimeRow>
            
            <TimeRow>
              <TimeUnit
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <TimeNumber>
                  {timeLeft.minutes.toString().padStart(2, '0')}
                </TimeNumber>
                <TimeLabel>Minutes</TimeLabel>
              </TimeUnit>
              
              <TimeUnit
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <TimeNumber>
                  {timeLeft.seconds.toString().padStart(2, '0')}
                </TimeNumber>
                <TimeLabel>Seconds</TimeLabel>
              </TimeUnit>
            </TimeRow>
          </CountdownTimer>
          
          <CountdownIllustration>
            <MatrixRain>
              {matrixColumns}
            </MatrixRain>
            
            <HackingTerminal
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <TerminalHeader>
                <TerminalButton $color="#FF5F57" />
                <TerminalButton $color="#FFBD2E" />
                <TerminalButton $color="#28CA42" />
                <TerminalTitle>BSides_Terminal_v2.5</TerminalTitle>
              </TerminalHeader>
              
              <TerminalContent>
                {terminalLines.map((line, index) => (
                  <TerminalLine
                    key={line.id || index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {line.type === 'prompt' && (
                      <span className="prompt">{line.text}</span>
                    )}
                    {line.type === 'command' && (
                      <span className="command">{line.text}</span>
                    )}
                    {line.type === 'output' && (
                      <span className="output">{line.text}</span>
                    )}
                    {line.type === 'error' && (
                      <span className="error">{line.text}</span>
                    )}
                    {line.type === 'success' && (
                      <span className="success">{line.text}</span>
                    )}
                    {line.type === 'highlight' && (
                      <span className="highlight">{line.text}</span>
                    )}
                  </TerminalLine>
                ))}
                <TerminalLine>
                  <BlinkingCursor>█</BlinkingCursor>
                </TerminalLine>
              </TerminalContent>
            </HackingTerminal>
          </CountdownIllustration>
        </CountdownContent>
      </div>
    </CountdownContainer>
  );
};

export default Countdown;